import { useState, useEffect } from "react";
import { settingsApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Plus, Trash2, Pencil, Save, X, Loader2, LayoutTemplate, ChevronDown, ChevronUp } from "lucide-react";
import { BLOG_TEMPLATES } from "@/utils/blogTemplates";

export interface CustomTemplate {
  id: string;
  name: string;
  description: string;
  content: string;
}

const SETTING_KEY = "blog_post_templates";

export function loadCustomTemplates(raw: string | undefined): CustomTemplate[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

// ── Empty form state ───────────────────────────────────────────────────────────
const EMPTY: Omit<CustomTemplate, "id"> = { name: "", description: "", content: "" };

// ── Sub-components ─────────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
    />
  );
}

// ── Template form (create / edit) ──────────────────────────────────────────────
interface TemplateFormProps {
  initial?: CustomTemplate;
  onSave: (t: Omit<CustomTemplate, "id">) => void;
  onCancel: () => void;
  saving: boolean;
}

function TemplateForm({ initial, onSave, onCancel, saving }: TemplateFormProps) {
  const [name,        setName]        = useState(initial?.name        ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [content,     setContent]     = useState(initial?.content     ?? "");

  const handleContent = (html: string) => setContent(html);

  return (
    <div className="border border-brand-primary/20 rounded-2xl p-5 space-y-4 bg-brand-primary/3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-800">{initial ? "Editar template" : "Novo template"}</p>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <Field label="Nome do template">
        <TextInput value={name} onChange={setName} placeholder="Ex: Artigo de saúde" />
      </Field>

      <Field label="Descrição curta">
        <TextInput value={description} onChange={setDescription} placeholder="Ex: Estrutura para artigos sobre saúde" />
      </Field>

      <Field label="Conteúdo do template">
        <RichTextEditor
          value={content}
          onChange={handleContent}
          placeholder="Escreva a estrutura do template. Use títulos, listas e parágrafos como guia para o redator..."
        />
      </Field>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={!name.trim() || saving}
          onClick={() => onSave({ name: name.trim(), description: description.trim(), content })}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Salvar template
        </button>
      </div>
    </div>
  );
}

// ── Template card ──────────────────────────────────────────────────────────────
interface TemplateCardProps {
  tpl: CustomTemplate;
  onEdit: () => void;
  onDelete: () => void;
  expanded: boolean;
  onToggle: () => void;
}

function TemplateCard({ tpl, onEdit, onDelete, expanded, onToggle }: TemplateCardProps) {
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-white">
        <div className="w-8 h-8 bg-brand-primary/8 rounded-lg flex items-center justify-center shrink-0">
          <LayoutTemplate className="w-4 h-4 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-800 truncate">{tpl.name}</p>
          {tpl.description && <p className="text-xs text-gray-400 truncate">{tpl.description}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggle}
            title="Ver conteúdo"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onEdit}
            title="Editar"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            title="Excluir"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
          <div
            className="prose prose-sm max-w-none text-gray-600 text-xs"
            dangerouslySetInnerHTML={{ __html: tpl.content || "<em class='text-gray-300'>Sem conteúdo</em>" }}
          />
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export function BlogTemplateManager() {
  const { toast } = useToast();
  const [templates,    setTemplates]    = useState<CustomTemplate[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [showForm,     setShowForm]     = useState(false);
  const [editingTpl,   setEditingTpl]   = useState<CustomTemplate | null>(null);
  const [expandedId,   setExpandedId]   = useState<string | null>(null);

  useEffect(() => {
    settingsApi.list().then(async (list: any[]) => {
      const row = list.find((s: any) => s.key === SETTING_KEY);
      const existing = loadCustomTemplates(row?.value);

      if (existing.length === 0) {
        // Seed built-in templates as custom templates on first load
        const seeded: CustomTemplate[] = BLOG_TEMPLATES.map(t => ({
          id: crypto.randomUUID(),
          name: t.label,
          description: t.description,
          content: t.content,
        }));
        await settingsApi.update(SETTING_KEY, JSON.stringify(seeded));
        setTemplates(seeded);
      } else {
        setTemplates(existing);
      }
    }).finally(() => setLoading(false));
  }, []);

  const persist = async (updated: CustomTemplate[]) => {
    setSaving(true);
    try {
      await settingsApi.update(SETTING_KEY, JSON.stringify(updated));
      setTemplates(updated);
      toast({ title: "Templates salvos!" });
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleCreate = async (data: Omit<CustomTemplate, "id">) => {
    const newTpl: CustomTemplate = { ...data, id: crypto.randomUUID() };
    await persist([...templates, newTpl]);
    setShowForm(false);
  };

  const handleUpdate = async (data: Omit<CustomTemplate, "id">) => {
    if (!editingTpl) return;
    const updated = templates.map(t => t.id === editingTpl.id ? { ...data, id: t.id } : t);
    await persist(updated);
    setEditingTpl(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Excluir este template?")) return;
    await persist(templates.filter(t => t.id !== id));
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-gray-800">Templates personalizados</p>
          <p className="text-xs text-gray-400 mt-0.5">Crie modelos de post para reutilizar com facilidade</p>
        </div>
        {!showForm && !editingTpl && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            Novo template
          </button>
        )}
      </div>

      {/* Create form */}
      {showForm && (
        <TemplateForm
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
          saving={saving}
        />
      )}

      {/* Template list */}
      {templates.length === 0 && !showForm ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-2xl">
          <LayoutTemplate className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Nenhum template criado ainda</p>
          <p className="text-xs text-gray-300 mt-1">Clique em "Novo template" ou importe uma sugestão abaixo</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(tpl => (
            editingTpl?.id === tpl.id ? (
              <TemplateForm
                key={tpl.id}
                initial={tpl}
                onSave={handleUpdate}
                onCancel={() => setEditingTpl(null)}
                saving={saving}
              />
            ) : (
              <TemplateCard
                key={tpl.id}
                tpl={tpl}
                expanded={expandedId === tpl.id}
                onToggle={() => setExpandedId(expandedId === tpl.id ? null : tpl.id)}
                onEdit={() => { setEditingTpl(tpl); setShowForm(false); }}
                onDelete={() => handleDelete(tpl.id)}
              />
            )
          ))}
        </div>
      )}

    </div>
  );
}
